import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import type { SearchFilters as UrlSearchFilters } from '~/apps/search/schemas'
import { getTransformObject } from '~/common/photos/transforms'
import type { Logger } from '~/composables/useGtmLogRequest'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { keysToCamel } from '~/utility/toCamel'
import type {
  CampsiteMapSearchResult,
  MapSearchCampsitesResults,
  SearchFilters,
} from '../schemas'
import { formatBounds } from './bounds'
import { fetchWithCache } from '../../../utility/fetchWithCache'

/** Raw API response from /search2/price/ (snake_case) */
interface PriceSearchResponse {
  results: PriceCampsiteRaw[]
  page: number
  num_pages: number
  count: number
  bounding_box?: [number, number][]
  title?: string
  heading?: string
  category?: unknown
  path?: string
  point?: { lat: number; lng: number }
  lead_price?: unknown
  sort_options: string[]
  sort?: string
  sort_field?: string
  result_max_price?: number
  within?: { mi: number; km: number }
  adults?: number
  child_ages?: number[]
  country?: string
  pitches_to_sell?: number
}

/** Raw campsite from price endpoint (snake_case, before keysToCamel) */
interface PriceCampsiteRaw {
  campsite_id: number
  category_ids?: number[]
  categories?: {
    id: string
    root_slug: string
    slug: string
    name: string
    order: number
    is_byo: boolean
  }[]
  hierarchy_text_short: string
  id?: number
  name: string
  path: string
  cheapest_price?: { amount: number; amount_minor: number; currency: string }
  point: { lat: number; lng: number }
  primary_photo?: {
    url: {
      master_image: string
      gallery_small?: string
      gallery_medium?: string
      gallery_large?: string
    }
    caption?: string
    title_attribution?: string
  } | null
  rate_count: number
  rating: string | number
  slug: string
  pitchtypes?: unknown[]
}

/** Raw campsite from price endpoint after keysToCamel */
interface PriceCampsiteCamel {
  campsiteId: number
  categoryIds?: number[]
  categories?: {
    id: string
    rootSlug: string
    slug: string
    name: string
    order: number
    isByo: boolean
  }[]
  hierarchyTextShort: string
  id?: number
  name: string
  path: string
  cheapestPrice?: { amount: number; amountMinor: number; currency: string }
  point: { lat: number; lng: number }
  primaryPhoto?: {
    url: {
      masterImage: string
      gallerySmall?: string
      galleryMedium?: string
      galleryLarge?: string
    }
    caption?: string
    titleAttribution?: string
  } | null
  rateCount: number
  rating: string | number
  slug: string
  pitchtypes?: unknown[]
}

export class FetchCampsitePriceMapSearchService {
  async search(
    filters: SearchFilters,
    langCode: string,
    _logger?: Logger,
    fetchOptions?: Record<string, unknown>,
  ): Promise<MapSearchCampsitesResults> {
    const params = this.filtersToParams(filters).toString()
    const fieldsToShow = [
      'campsite_id',
      'categories',
      'category_ids',
      'hierarchy_text_short',
      'id',
      'name',
      'path',
      'cheapest_price',
      'point',
      'primary_photo',
      'rate_count',
      'rating',
      'rating100',
      'slug',
      'url',
    ]
    const url = getLangFallbackUrl(
      langCode,
      `/search2/price/?fields=${fieldsToShow.join(
        ',',
      )}&${params}&sort=magic&facet_counts=none&seo=0`,
    )

    const response = await fetchWithCache<PriceSearchResponse>(
      url,
      fetchOptions,
    )
    return {
      bounds: response.bounding_box
        ? formatBounds(
            response.bounding_box as [[number, number], [number, number]],
          )
        : null,
      campsites: this.getFilteredResults(
        // keysToCamel transforms snake_case → camelCase at runtime; the cast bridges
        // the structural gap between PriceCampsiteRaw (snake_case nested keys like
        // root_slug, master_image) and PriceCampsiteCamel (camelCase equivalents).
        this.formatResults(
          keysToCamel(response.results) as unknown as PriceCampsiteCamel[],
        ),
      ),
      pagination: {
        currentPage: Number(response.page),
        totalPages: response.num_pages,
        totalCount: response.count,
      },
      title: response.title,
      heading: response.heading,
      bookable: true,
      category: response.category,
      count: response.count,
      path: response.path,
      point: response.point,
      leadPrice: response.lead_price,
      sortOptions: response.sort_options,
      sortOption: response.sort || response.sort_field || '',
      maxPrice: response.result_max_price,
      within: keysToCamel(response.within),
    }
  }

  private getFilteredResults(
    results: CampsiteMapSearchResult[],
  ): CampsiteMapSearchResult[] {
    return results.filter((r) => r.name !== 'noindex')
  }

  private formatResults(
    results: PriceCampsiteCamel[],
  ): CampsiteMapSearchResult[] {
    return results
      .filter((campsite) => !!campsite.point)
      .map((campsite): CampsiteMapSearchResult => {
        const { lat, lng } = campsite.point
        return {
          id: campsite.campsiteId.toString(),
          name: campsite.name,
          path: campsite.path,
          slug: campsite.slug,
          categories: campsite.categories,
          rateCount: campsite.rateCount,
          status: 'bookable',
          bookable: true,
          price: campsite.cheapestPrice,
          hierarchy: campsite.hierarchyTextShort,
          rating: Number(campsite.rating),
          rating10: Number(campsite.rating) * 2,
          point: { lat, lng },
          lat,
          lng,
          leadPriceNights: null,
          leadPriceAdults: null,
          photo: campsite.primaryPhoto
            ? {
                url: getTransformObject(
                  'map',
                  campsite.primaryPhoto.url.masterImage,
                ),
                caption: campsite.primaryPhoto.caption ?? '',
              }
            : null,
        }
      })
  }

  private filtersToParams(filters: SearchFilters) {
    if (!filters.dates) {
      throw new Error('no dates')
    }
    const params = new URLSearchParams()
    for (const param of parseSearchFiltersToUrl(filters as UrlSearchFilters)) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      if (params.get('path')) {
        params.delete('bounds')
      }
    }
    if (filters.engine) {
      params.set('engine', filters.engine.toString())
    }
    return params
  }
}
